import { Controller } from '@hotwired/stimulus'
import debounce from 'lodash/debounce'

/* global DOMParser */

export default class extends Controller {
  static values = {
    url: String,
    delay: { type: Number, default: 400 }
  }

  connect () {
    this.lastFetchedUrl = null
    this.schedule = debounce(() => this.reload(), this.delayValue)
    this.reload()
  }

  disconnect () {
    this.schedule.cancel()
  }

  queue () {
    this.markBusy()
    this.schedule()
  }

  settle (event) {
    const quoteFrame = this.quoteFrame
    if (!quoteFrame || event.target !== quoteFrame) return

    quoteFrame.removeAttribute('aria-busy')
  }

  reload () {
    const frames = this.refreshableFrames
    if (frames.length === 0 || !this.hasUrlValue) return

    const url = this.buildUrl()
    if (this.lastFetchedUrl === url.toString()) {
      this.clearBusy()
      return
    }

    this.markBusy()
    this.lastFetchedUrl = url.toString()

    fetch(url.toString(), {
      headers: { Accept: 'text/html' },
      credentials: 'same-origin'
    })
      .then((response) => response.text())
      .then((html) => {
        this.applyFrameUpdates(html)
        this.clearBusy()
        this.quoteFrame?.dispatchEvent(new Event('turbo:frame-load', { bubbles: true }))
      })
      .catch(() => {
        this.clearBusy()
        this.lastFetchedUrl = null
      })
  }

  applyFrameUpdates (html) {
    const doc = new DOMParser().parseFromString(html, 'text/html')

    this.refreshableFrames.forEach((frame) => {
      const updated = doc.getElementById(frame.id)
      if (updated) frame.replaceWith(updated)
    })
  }

  buildUrl () {
    const params = new URLSearchParams()
    new FormData(this.element).forEach((value, key) => {
      if (key === 'authenticity_token' || key === 'utf8') return
      params.append(key, value)
    })
    const url = new URL(this.urlValue, window.location.origin)
    url.search = params.toString()
    return url
  }

  markBusy () {
    this.refreshableFrames.forEach((frame) => frame.setAttribute('aria-busy', 'true'))
  }

  clearBusy () {
    this.refreshableFrames.forEach((frame) => frame.removeAttribute('aria-busy'))
  }

  get refreshableFrames () {
    return [...this.element.querySelectorAll('[data-debounced-frame-src-target="frame"]')]
  }

  get quoteFrame () {
    return this.refreshableFrames.find((frame) => frame.id.endsWith('domofinance_quote')) ?? null
  }
}
